import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Start', to: '/' },
  { label: 'Love Code', to: '/beziehungsheilung-fuer-frauen' },
  { label: 'Business Code', to: '/female-leadership-fuer-frauen' },
  { label: 'Shop', to: '/shop' },
  { label: 'Anmelden', to: '/contact' },
];

const GlowChar = ({ char, delay }) => (
  <motion.span
    initial={{ opacity: 0, filter: 'blur(20px)', y: 30 }}
    animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    className="inline-block"
    style={{ textShadow: '0 0 30px rgba(212,175,55,0.5)' }}
  >
    {char === ' ' ? '\u00A0' : char}
  </motion.span>
);

const RevealHeadline = ({ text, delay = 0, className = '' }) => (
  <span className={className}>
    {text.split('').map((char, i) => (
      <GlowChar key={i} char={char} delay={delay + i * 0.03} />
    ))}
  </span>
);

const LoveShiftHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex flex-col overflow-hidden">
      {/* Nav */}
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.2 }}
        className="relative z-50 flex items-center justify-between px-8 py-6 border-b border-white/5 bg-dark/60 backdrop-blur-xl"
      >
        <Link to="/" className="text-gold font-serif text-xl tracking-widest" style={{ textShadow: '0 0 15px rgba(212,175,55,0.6)' }}>
          CF.
        </Link>
        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map(l => (
            <Link key={l.label} to={l.to} className="text-xs uppercase tracking-widest text-cream/60 hover:text-gold transition-colors duration-500">
              {l.label}
            </Link>
          ))}
          <span className="text-xs uppercase tracking-widest text-gold border border-gold/40 px-4 py-2 font-semibold" style={{ textShadow: '0 0 10px rgba(212,175,55,0.4)' }}>
            Love Shift Masterclass
          </span>
        </div>
      </motion.nav>

      {/* BG Layer */}
      <motion.div style={{ y }} className="absolute inset-0 z-0 scale-110">
        <div className="absolute inset-0 bg-dark" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,rgba(212,175,55,0.07)_0%,transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_80%,rgba(114,47,55,0.12)_0%,transparent_55%)]" />
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gold"
            style={{
              width: Math.random() * 3 + 1,
              height: Math.random() * 3 + 1,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.3,
            }}
            animate={{ y: [-20, 20, -20], opacity: [0.1, 0.5, 0.1] }}
            transition={{ duration: 4 + Math.random() * 4, repeat: Infinity, delay: Math.random() * 3 }}
          />
        ))}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            backgroundSize: '200px',
          }}
        />
      </motion.div>

      {/* Hero Content */}
      <motion.div style={{ opacity }} className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 py-24">
        <motion.div
          initial={{ opacity: 0, letterSpacing: '0.05em' }}
          animate={{ opacity: 1, letterSpacing: '0.4em' }}
          transition={{ duration: 2, delay: 0.4 }}
          className="text-gold text-xs uppercase font-semibold mb-8"
          style={{ textShadow: '0 0 20px rgba(212,175,55,0.8)' }}
        >
          Live Masterclass · 17. April 2026 · 17:00 Uhr
        </motion.div>

        <h1 className="text-6xl md:text-8xl lg:text-9xl font-serif leading-none mb-6">
          <RevealHeadline text="LOVE" delay={0.6} className="block text-cream" />
          <RevealHeadline text="SHIFT" delay={1.2} className="block text-transparent bg-clip-text bg-gold-gradient" />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 2.4 }}
          className="text-lg md:text-2xl font-serif italic text-cream/80 max-w-3xl mx-auto mb-4 leading-relaxed"
        >
          Die Affäre. Das Hin und Her. Die Verbindung, die sich richtig anfühlt –{' '}
          <span className="text-wine-light">aber dich zerstört.</span>
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2.8 }}
          className="text-base md:text-lg text-cream/55 max-w-2xl mx-auto mb-14 font-light leading-relaxed"
        >
          Das Problem ist nicht die Verbindung. Es ist das unbewusste Muster darunter.
          In 90 Minuten entschlüsseln wir es, verstehen es und lösen es auf – ohne Drama.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 3.2 }}
        >
          <motion.a
            href="#anmelden"
            whileHover={{ scale: 1.06, boxShadow: '0 0 60px rgba(212,175,55,0.7), 0 0 120px rgba(212,175,55,0.3)' }}
            whileTap={{ scale: 0.97 }}
            className="inline-block px-14 py-5 bg-gold-gradient text-dark font-bold uppercase tracking-widest text-sm cursor-pointer transition-all duration-700"
            style={{ boxShadow: '0 0 30px rgba(212,175,55,0.4)', borderRadius: '2px' }}
          >
            Kostenlos anmelden →
          </motion.a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3.8 }}
          className="text-xs text-cream/30 mt-6 tracking-widest uppercase"
        >
          Begrenzte Plätze · Kein Replay garantiert
        </motion.p>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 14, 0], opacity: [0.2, 1, 0.2] }}
        transition={{ repeat: Infinity, duration: 3.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
      >
        <div className="w-1 h-1 rounded-full bg-gold" style={{ boxShadow: '0 0 12px rgba(212,175,55,1)' }} />
        <div className="w-[1px] h-16 bg-gradient-to-b from-gold via-gold/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default LoveShiftHero;